// src/components/SettingsControls.js - Save/Load settings controls

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { SettingsManager } from '../utils/settingsManager';

const SettingsControls = ({ 
  playerState, 
  playerActions,
  onStatusUpdate,
  getCurrentFileContent,
  loadMusicXMLFromContent,
  hasLoadedFile,
  currentPresetName,
  isLoading
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [savedPresets, setSavedPresets] = useState({});
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  // const [debugInfo, setDebugInfo] = useState('');
  // const [debugHistory, setDebugHistory] = useState([]);
  
  // Track the last saved state to detect changes
  const lastSavedStateRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const pendingAutoSaveRef = useRef(false); // Track if auto-save is needed after loading

  // Toast notification helper
  const showToastNotification = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000); // Hide after 3 seconds
  }, []);

  // Update presets list when dialog opens
  const refreshPresets = useCallback(async () => {
    if (!user?.id) {
      setSavedPresets({});
      return;
    }

    try {
      setLoading(true);
      const presets = await SettingsManager.getPresetsForCurrentDevice(user.id);
      setSavedPresets(presets);
    } catch (error) {
      console.error('Error refreshing presets:', error);
      setSavedPresets({});
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load presets on component mount
  useEffect(() => {
    refreshPresets();
  }, [refreshPresets]);

  // Initialize lastSavedStateRef when there's already a preset loaded
  useEffect(() => {
    if (currentPresetName && hasLoadedFile && !lastSavedStateRef.current) {
      // console.log('Initializing lastSavedStateRef for existing preset:', currentPresetName);
      
      // We need to load the actual preset to get the complete settings with device-specific data
      const initializeFromPreset = async () => {
        try {
          const loadedSettings = await SettingsManager.loadPreset(currentPresetName, user?.id);
          if (loadedSettings) {
            // Convert loaded settings to current format for comparison
            const fileContent = getCurrentFileContent ? getCurrentFileContent() : null;
            const normalizedSettings = SettingsManager.collectCurrentSettings(
              {
                playbackSpeed: loadedSettings.playbackSpeed || 1,
                volume: loadedSettings.volume || 0,
                instrument: loadedSettings.instrument || 'piano',
                audioLatency: loadedSettings.audioLatency || 0,
                partControls: loadedSettings.partControls || [],
                partVolumes: loadedSettings.partVolumes || {},
                zoom: loadedSettings.zoom || 1,
                autoScroll: loadedSettings.autoScroll !== false,
                lookaheadBeats: loadedSettings.lookaheadBeats || 0,
                fileName: loadedSettings.fileName || `${currentPresetName.toLowerCase()}.musicxml`
              },
              fileContent,
              loadedSettings
            );
            lastSavedStateRef.current = JSON.stringify(normalizedSettings);
            lastLoadedSettingsRef.current = loadedSettings;
          } else {
            // Fallback to current settings if load fails
            const fileContent = getCurrentFileContent ? getCurrentFileContent() : null;
            const currentSettings = SettingsManager.collectCurrentSettings(playerState, fileContent);
            lastSavedStateRef.current = JSON.stringify(currentSettings);
            lastLoadedSettingsRef.current = currentSettings;
            // console.log('lastSavedStateRef initialized from current state (fallback)');
          }
        } catch (error) {
          console.error('Error initializing from preset:', error);
          // Fallback to current settings
          const fileContent = getCurrentFileContent ? getCurrentFileContent() : null;
          const currentSettings = SettingsManager.collectCurrentSettings(playerState, fileContent);
          lastSavedStateRef.current = JSON.stringify(currentSettings);
          lastLoadedSettingsRef.current = currentSettings;
        }
      };
      
      initializeFromPreset();
    }
  }, [currentPresetName, hasLoadedFile, playerState, getCurrentFileContent, user?.id]);

  // Track existing settings to avoid loading during auto-save
  const lastLoadedSettingsRef = useRef(null);

  // Auto-save functionality
  const autoSave = useCallback(async () => {
    // console.log('autoSave called:', { 
    //   currentPresetName, 
    //   canSave: SettingsManager.canSaveSettings(user), 
    //   autoSaving,
    //   userId: user?.id
    // });

    if (!currentPresetName || !SettingsManager.canSaveSettings(user) || autoSaving) {
      // console.log('Auto-save conditions not met');
      return;
    }

    try {
      // console.log('Starting auto-save to preset:', currentPresetName);
      setAutoSaving(true);
      const fileContent = getCurrentFileContent ? getCurrentFileContent() : null;
      
      // Use cached existing settings to avoid loading during auto-save loop
      const existingSettings = lastLoadedSettingsRef.current;
      
      const settings = SettingsManager.collectCurrentSettings(playerState, null, existingSettings);
      
      await SettingsManager.savePreset(currentPresetName, settings, user.id);
      
      // Update the lastSavedStateRef and preserve the full settings for device-specific data
      lastSavedStateRef.current = JSON.stringify(settings);
      // Keep the complete settings with both device types preserved
      lastLoadedSettingsRef.current = settings;
      
      // setDebugInfo(`AUTO-SAVE: ✅ Completed! Saved zoom: ${settings.zoom}`);
      onStatusUpdate(`Auto-saved to ${currentPresetName}`);
      setTimeout(() => {
        onStatusUpdate('');
        // setDebugInfo('');
      }, 3000);
    } catch (error) {
      // setDebugInfo(`AUTO-SAVE: ❌ Failed - ${error.message}`);
      onStatusUpdate(`Auto-save failed: ${error.message}`);
      setTimeout(() => onStatusUpdate(''), 3000);
    } finally {
      setAutoSaving(false);
    }
  }, [currentPresetName, playerState, getCurrentFileContent, user, autoSaving, onStatusUpdate]);

  // Track previous values to identify what changed
  const prevDepsRef = useRef({});
  
  // Monitor player state changes for auto-save
  useEffect(() => {
    const currentDeps = {
      playbackSpeed: playerState.playbackSpeed,
      volume: playerState.volume,
      instrument: playerState.instrument,
      audioLatency: playerState.audioLatency,
      partVolumes: JSON.stringify(playerState.partVolumes),
      partControls: JSON.stringify(playerState.partControls),
      // Round zoom to 3 decimal places to ignore Mobile Safari rendering nudges (±0.001)
      zoom: Math.round(playerState.zoom * 1000) / 1000,
      autoScroll: playerState.autoScroll,
      lookaheadBeats: playerState.lookaheadBeats,
      transposeSteps: playerState.transposeSteps,
      hasLoadedFile,
      currentPresetName,
      isLoading
    };
    
    // Compare with previous values to see what changed
    const changedDeps = [];
    Object.keys(currentDeps).forEach(key => {
      if (prevDepsRef.current[key] !== currentDeps[key]) {
        changedDeps.push({
          dep: key,
          from: prevDepsRef.current[key],
          to: currentDeps[key]
        });
      }
    });
    
    prevDepsRef.current = currentDeps;
    
    // console.log('🔍 Auto-save effect triggered by:', changedDeps.length ? changedDeps : 'Initial render');
    // if (changedDeps.length > 0) {
    //   changedDeps.forEach(change => {
    //     console.log(`  ${change.dep}: ${change.from} → ${change.to}`);
    //   });
    // }

    // CRITICAL: NEVER auto-save during loading operations
    if (isLoading) {
      // console.log('Auto-save BLOCKED: loading in progress');
      
      // But check if we need to mark changes for post-loading auto-save
      if (currentPresetName && hasLoadedFile && lastSavedStateRef.current) {
        const fileContent = getCurrentFileContent ? getCurrentFileContent() : null;
        const existingSettings = lastLoadedSettingsRef.current;
        const currentSettings = SettingsManager.collectCurrentSettings(playerState, fileContent, existingSettings);
        const currentStateString = JSON.stringify(currentSettings);
        
        if (lastSavedStateRef.current !== currentStateString) {
          // const msg = `LOADING: Changes detected, marking for auto-save. Zoom: ${playerState.zoom}`;
          // setDebugInfo(msg);
          // setDebugHistory(prev => [msg, ...prev.slice(0, 4)]);
          pendingAutoSaveRef.current = true;
        } else {
          // setDebugInfo(`LOADING: No changes detected. Zoom: ${playerState.zoom}`);
        }
      }
      return;
    }

    // Check for pending auto-save after loading completes
    if (pendingAutoSaveRef.current && currentPresetName && hasLoadedFile) {
      // const msg = `EXECUTING: Post-loading auto-save for ${currentPresetName}. Zoom: ${playerState.zoom}`;
      // setDebugInfo(msg);
      // setDebugHistory(prev => [msg, ...prev.slice(0, 4)]);
      pendingAutoSaveRef.current = false;
      
      // Clear any existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      
      // Execute auto-save after a short delay to ensure all state updates are complete
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 500); // Shorter delay since loading just completed
      return;
    }

    // DEBUGGING: Add a manual way to see current state vs saved state
    if (currentPresetName && hasLoadedFile && lastSavedStateRef.current) {
      const fileContent = getCurrentFileContent ? getCurrentFileContent() : null;
      const existingSettings = lastLoadedSettingsRef.current;
      const currentSettings = SettingsManager.collectCurrentSettings(playerState, fileContent, existingSettings);
      const currentStateString = JSON.stringify(currentSettings);
      
      if (lastSavedStateRef.current !== currentStateString) {
        // console.log('🔍 DEBUGGING: State difference detected but not during loading:');
        // console.log('Current zoom in playerState:', playerState.zoom);
        // console.log('Current auto-scroll in playerState:', playerState.autoScroll);
        // console.log('Saved vs Current state lengths:', lastSavedStateRef.current.length, 'vs', currentStateString.length);
        
        // Show what's actually different
        try {
          const savedSettings = JSON.parse(lastSavedStateRef.current);
          const currentSettings = JSON.parse(currentStateString);
          // console.log('Saved zoom:', savedSettings.zoom, 'Current zoom:', currentSettings.zoom);
          // console.log('Saved zoom:', savedSettings.zoom);
          // console.log('Current zoom:', currentSettings.zoom);
          
          // Find the actual difference
          // console.log('🔍 FIRST 200 CHARS OF SAVED:', lastSavedStateRef.current.substring(0, 200));
          // console.log('🔍 FIRST 200 CHARS OF CURRENT:', currentStateString.substring(0, 200));
          // console.log('🔍 LAST 200 CHARS OF SAVED:', lastSavedStateRef.current.substring(lastSavedStateRef.current.length - 200));
          // console.log('🔍 LAST 200 CHARS OF CURRENT:', currentStateString.substring(currentStateString.length - 200));
        } catch (e) {
          // console.log('Could not parse settings for comparison');
        }
      }
    }

    if (!currentPresetName || !hasLoadedFile) {
      // console.log('Auto-save skipped: no preset or no file loaded');
      // Clear the last saved state if no preset is loaded
      if (!currentPresetName) {
        lastSavedStateRef.current = null;
        pendingAutoSaveRef.current = false;
      }
      return;
    }

    // Skip autosave if this appears to be a temporary state change (like measure selection)
    // by only auto-saving when the change seems intentional (significant state change)
    if (!lastSavedStateRef.current) {
      // First save after loading - store current state without triggering save
      const fileContent = getCurrentFileContent ? getCurrentFileContent() : null;
      const existingSettings = lastLoadedSettingsRef.current;
      const currentSettings = SettingsManager.collectCurrentSettings(playerState, fileContent, existingSettings);
      const currentStateString = JSON.stringify(currentSettings);
      
      lastSavedStateRef.current = currentStateString;
      // console.log('Auto-save: Initial state captured, no save triggered');
      return;
    }

    const fileContent = getCurrentFileContent ? getCurrentFileContent() : null;
    // CRITICAL: Use same existingSettings as auto-save to ensure consistent JSON for comparison
    const existingSettings = lastLoadedSettingsRef.current;
    const currentSettings = SettingsManager.collectCurrentSettings(playerState, fileContent, existingSettings);
    const currentStateString = JSON.stringify(currentSettings);

    // console.log('Auto-save checking for changes:', {
    //   hasLastSaved: !!lastSavedStateRef.current,
    //   statesEqual: lastSavedStateRef.current === currentStateString,
    //   currentStateString: currentStateString.slice(0, 200) + '...'
    // });
    
    if (lastSavedStateRef.current && lastSavedStateRef.current !== currentStateString) {
      // console.log('AUTO-SAVE TRIGGERED: Settings changed');
      
      // Find what exactly changed
      const savedSettings = lastSavedStateRef.current ? JSON.parse(lastSavedStateRef.current) : {};
      const currentSettingsObj = currentSettings;
      
      // Object.keys(currentSettingsObj).forEach(key => {
      //   if (JSON.stringify(savedSettings[key]) !== JSON.stringify(currentSettingsObj[key])) {
      //     console.log(`CHANGED SETTING: ${key}`, {
      //       old: savedSettings[key],
      //       new: currentSettingsObj[key]
      //     });
      //     
      //     // Show detailed diff for arrays, especially partControls
      //     if (Array.isArray(currentSettingsObj[key]) && Array.isArray(savedSettings[key])) {
      //       console.log(`Array length difference: ${savedSettings[key].length} -> ${currentSettingsObj[key].length}`);
      //       currentSettingsObj[key].forEach((item, index) => {
      //         if (JSON.stringify(item) !== JSON.stringify(savedSettings[key][index])) {
      //           console.log(`Array item ${index} changed:`, {
      //             old: savedSettings[key][index],
      //             new: item
      //           });
      //           
      //           // For partControls, show property-by-property diff
      //           if (key === 'partControls' && typeof item === 'object' && typeof savedSettings[key][index] === 'object') {
      //             console.log(`  PartControl ${index} (${item.name || item.id}) changes:`);
      //             
      //             // Show all properties in both objects
      //             const allProps = new Set([...Object.keys(item), ...Object.keys(savedSettings[key][index] || {})]);
      //             console.log(`    All properties in new:`, Object.keys(item));
      //             console.log(`    All properties in old:`, Object.keys(savedSettings[key][index] || {}));
      //             
      //             allProps.forEach(prop => {
      //               const oldVal = savedSettings[key][index][prop];
      //               const newVal = item[prop];
      //               if (oldVal !== newVal) {
      //                 console.log(`    ${prop}: ${oldVal} -> ${newVal}`);
      //               }
      //             });
      //           }
      //         }
      //       });
      //     }
      //   }
      // });
    }

    // Check if state has actually changed
    if (lastSavedStateRef.current && lastSavedStateRef.current !== currentStateString) {
      // const msg = `NORMAL: State changed, scheduling auto-save. Zoom: ${playerState.zoom}`;
      // setDebugInfo(msg);
      // setDebugHistory(prev => [msg, ...prev.slice(0, 4)]);
      
      // Clear any existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Debounce auto-save to avoid too frequent saves
      autoSaveTimeoutRef.current = setTimeout(() => {
        // const saveMsg = `NORMAL: Executing auto-save for ${currentPresetName}. Zoom: ${playerState.zoom}`;
        // setDebugInfo(saveMsg);
        // setDebugHistory(prev => [saveMsg, ...prev.slice(0, 4)]);
        autoSave();
      }, 1000); // Auto-save 1 second after last change
    } else if (!lastSavedStateRef.current) {
      // const msg = `NORMAL: No last saved state found. Zoom: ${playerState.zoom}`;
      // setDebugInfo(msg);
      // setDebugHistory(prev => [msg, ...prev.slice(0, 4)]);
    } else {
      // const msg = `NORMAL: No changes detected. Zoom: ${playerState.zoom}`;
      // setDebugInfo(msg);
      // setDebugHistory(prev => [msg, ...prev.slice(0, 4)]);
    }

    // Cleanup timeout on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [
    // Only monitor settings that the USER explicitly changes, not internal state changes
    playerState.playbackSpeed,
    playerState.volume, 
    playerState.instrument,
    playerState.audioLatency,
    // Remove partControls from dependencies - monitor only when they actually change through user interaction
    // partControls will be monitored through the actual settings collection comparison instead
    playerState.partVolumes,
    playerState.zoom,
    playerState.autoScroll,
    playerState.lookaheadBeats,
    playerState.transposeSteps,
    // Keep hasLoadedFile as it affects when autosave should run
    hasLoadedFile,
    currentPresetName, 
    isLoading, 
    autoSave, 
    getCurrentFileContent
  ]);


  const handleSaveAs = useCallback(() => {
    if (!SettingsManager.canSaveSettings(user)) {
      showToastNotification(SettingsManager.getSignInMessage());
      return;
    }
    refreshPresets();
    setSaveAsName('');
    setShowSaveDialog(true);
  }, [refreshPresets, user, showToastNotification]);

  const handleSaveAsConfirm = useCallback(async () => {
    if (!saveAsName.trim()) return;

    try {
      setLoading(true);
      const fileContent = getCurrentFileContent ? getCurrentFileContent() : null;
      
      // For Save As, we don't preserve existing settings since it's a new preset
      const settings = SettingsManager.collectCurrentSettings(playerState, null);
      await SettingsManager.savePreset(saveAsName.trim(), settings, user.id);
      
      if (playerActions.setCurrentPresetName) {
        playerActions.setCurrentPresetName(saveAsName.trim());
      }
        
      // Initialize the last saved state for auto-save functionality
      const settingsString = JSON.stringify(settings);
      lastSavedStateRef.current = settingsString;
      lastLoadedSettingsRef.current = settings;
        
        showToastNotification(`✓ ${t('messages.savedAs', { preset: saveAsName.trim() })}`);
        onStatusUpdate(t('messages.savedAs', { preset: saveAsName.trim() }));
        setShowSaveDialog(false);
        setSaveAsName('');
        refreshPresets(); // Update presets list to refresh Load button state
    } catch (error) {
      console.error('Save As error:', error);
      showToastNotification(`✗ ${t('messages.errorSaving')}: ${error.message}`);
      onStatusUpdate(t('messages.errorSaving'));
    } finally {
      setLoading(false);
    }
  }, [saveAsName, playerState, onStatusUpdate, getCurrentFileContent, showToastNotification, user?.id, refreshPresets, t, playerActions]);

  const handleLoad = useCallback(() => {
    refreshPresets();
    setShowLoadDialog(true);
  }, [refreshPresets]);

  const handleLoadPreset = useCallback(async (presetName) => {
    console.log(`[iOS Debug] FUNCTION CALLED: handleLoadPreset with ${presetName}`);
    if (!user?.id) {
      onStatusUpdate('Must be signed in to load presets');
      return;
    }

    // Set up global timeout to prevent infinite loading
    const globalTimeoutId = setTimeout(() => {
      if (playerActions.setIsLoading) {
        playerActions.setIsLoading(false);
      }
      onStatusUpdate(`Loading timeout: ${presetName} failed to load`);
    }, 10000); // 10 second global timeout

    try {
      const settings = await SettingsManager.loadPreset(presetName, user.id);
      
      if (settings) {
        try {
          setShowLoadDialog(false);
          onStatusUpdate(`Loading preset: ${presetName}...`);
          
          // First, load the MusicXML file if it exists
          if (settings.fileName && loadMusicXMLFromContent) {
            try {
              // Download XML content from Vercel Blob storage using filename
              const xmlContent = await SettingsManager.downloadXMLFromBlob(settings.fileName);
              
              // Mark this as a settings load so completion waits for visibility sync
              await loadMusicXMLFromContent(xmlContent, settings.fileName, true);
              
              // Wait longer for parts to be created and UI to update
              await new Promise(resolve => setTimeout(resolve, 500));
            } catch (error) {
              console.error(`Failed to load XML file from blob storage: ${settings.fileName}`, error);
              onStatusUpdate(`Error: Could not load music file "${settings.fileName}" from cloud storage. ${error.message}`);
              showToastNotification(`Could not load music file: ${settings.fileName}`);
              clearTimeout(globalTimeoutId);
              if (playerActions.setIsLoading) {
                playerActions.setIsLoading(false);
              }
              return;
            }
          } else if (settings.fileContent && loadMusicXMLFromContent) {
            // Legacy support for presets that still have embedded content
            await loadMusicXMLFromContent(settings.fileContent, settings.fileName, true);
            
            // Wait longer for parts to be created and UI to update
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        // Set up render completion callback with timeout fallback
        let renderTimeoutId = null;
        if (playerActions.osmdRef?.current) {
          // Set timeout fallback for mobile browsers where render callback may not fire
          renderTimeoutId = setTimeout(() => {
            clearTimeout(globalTimeoutId);
            if (playerActions.setIsLoading) {
              playerActions.setIsLoading(false);
            }
            onStatusUpdate('');
          }, 5000); // 5 second timeout
          
          playerActions.osmdRef.current._onRenderComplete = () => {
            // Clear both timeouts since callback fired successfully
            if (renderTimeoutId) {
              clearTimeout(renderTimeoutId);
              renderTimeoutId = null;
            }
            clearTimeout(globalTimeoutId);
            // Clear loading state after score rendering is complete
            if (playerActions.setIsLoading) {
              playerActions.setIsLoading(false);
            }
            onStatusUpdate('');
          };
        }
        
        // Then apply all the settings
        const success = SettingsManager.applySettings(settings, playerActions);
        
        if (success) {
          if (playerActions.setCurrentPresetName) {
            playerActions.setCurrentPresetName(presetName);
          }
          
          // Initialize the last saved state for auto-save functionality
          const settingsString = JSON.stringify(settings);
          lastSavedStateRef.current = settingsString;
          lastLoadedSettingsRef.current = settings;
          
          // If no render callback was set up (no OSMD ref), clear loading immediately
          if (!playerActions.osmdRef?.current?._onRenderComplete && !renderTimeoutId) {
            setTimeout(() => {
              if (playerActions.setIsLoading) {
                playerActions.setIsLoading(false);
              }
              onStatusUpdate('');
            }, 100); // Minimal delay for UI updates
          }
        } else {
          // Clear any pending callback and loading state on error
          clearTimeout(globalTimeoutId);
          if (playerActions.osmdRef?.current) {
            playerActions.osmdRef.current._onRenderComplete = null;
          }
          if (playerActions.setIsLoading) {
            playerActions.setIsLoading(false);
          }
          onStatusUpdate('Error applying preset settings');
        }
      } catch (error) {
        clearTimeout(globalTimeoutId);
        if (playerActions.setIsLoading) {
          playerActions.setIsLoading(false);
        }
        onStatusUpdate(`Error loading preset: ${error.message}`);
      }
    } else {
      clearTimeout(globalTimeoutId);
      if (playerActions.setIsLoading) {
        playerActions.setIsLoading(false);
      }
      onStatusUpdate('Error loading preset');
    }
    } catch (outerError) {
      clearTimeout(globalTimeoutId);
      if (playerActions.setIsLoading) {
        playerActions.setIsLoading(false);
      }
      onStatusUpdate(`Error loading preset: ${outerError.message}`);
    }
  }, [playerActions, onStatusUpdate, loadMusicXMLFromContent, user?.id]);

  const handleDeletePreset = useCallback(async (presetName) => {
    if (!user?.id) {
      onStatusUpdate(t('settings.mustSignIn'));
      return;
    }

    if (window.confirm(t('settings.deletePresetConfirm', { preset: presetName }))) {
      try {
        setLoading(true);
        await SettingsManager.deletePreset(presetName, user.id);
        onStatusUpdate(`Deleted preset: ${presetName}`);
        if (currentPresetName === presetName && playerActions.setCurrentPresetName) {
          playerActions.setCurrentPresetName('');
        }
        await refreshPresets();
      } catch (error) {
        console.error('Delete preset error:', error);
        onStatusUpdate(`Error deleting preset: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  }, [currentPresetName, onStatusUpdate, refreshPresets, user?.id, playerActions]);

  const presetCount = Object.keys(savedPresets).length;

  return (
    <>
      {/* Main Buttons - Hidden per user request, auto-save still active */}
      <div className="settings-controls-inline" style={{
        display: 'none', // Hidden: manual settings buttons not needed with auto-save
        alignItems: 'center',
        gap: '8px',
        position: 'relative'
      }}>

        <button
          onClick={handleSaveAs}
          disabled={!hasLoadedFile}
          title={t('tooltips.saveAs')}
          style={{
            padding: '8px 12px',
            fontSize: '12px',
            backgroundColor: !hasLoadedFile ? '#6c757d' : '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: !hasLoadedFile ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: !hasLoadedFile ? 0.6 : 1
          }}
        >
          💾+
        </button>

        {/* Auto-save indicator */}
        {autoSaving && (
          <div style={{
            position: 'absolute',
            top: '-25px',
            right: '0px',
            fontSize: '10px',
            color: '#6c757d',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1000,
            pointerEvents: 'none'
          }}>
            <span>💾</span>
            <span>Auto-saving...</span>
          </div>
        )}

        <button
          onClick={handleLoad}
          title={t('tooltips.loadPreset')}
          disabled={presetCount === 0}
          style={{
            padding: '8px 12px',
            fontSize: '12px',
            backgroundColor: presetCount === 0 ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: presetCount === 0 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: presetCount === 0 ? 0.6 : 1
          }}
        >
          📂
        </button>

      </div>

      {/* Save As Dialog */}
      {showSaveDialog && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{t('settings.saveAsTitle')}</h3>
            <input
              type="text"
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              placeholder={t('settings.presetNamePlaceholder')}
              maxLength={50}
              autoFocus
            />
            <div className="modal-buttons">
              <button onClick={handleSaveAsConfirm} disabled={!saveAsName.trim()}>
                {t('controls.save')}
              </button>
              <button onClick={() => setShowSaveDialog(false)}>
                {t('controls.cancel')}
              </button>
            </div>
            
            {Object.keys(savedPresets).length > 0 && (
              <div className="existing-presets">
                <h4>{t('settings.existingPresetsTitle')}</h4>
                <ul>
                  {Object.keys(savedPresets).map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Load Dialog */}
      {showLoadDialog && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{t('settings.loadSettings')}</h3>
            
            {Object.keys(savedPresets).length === 0 ? (
              <p>{t('settings.noPresets')}</p>
            ) : (
              <div className="presets-list">
                {Object.entries(savedPresets).map(([name, preset]) => (
                  <div key={name} className="preset-item">
                    <div className="preset-info">
                      <div className="preset-name">{name}</div>
                      <div className="preset-details">
                        {t('settings.saved')}: {new Date(preset.savedAt).toLocaleDateString()}
                        {preset.fileName && ` • ${t('settings.file')}: ${preset.fileName}`}
                      </div>
                    </div>
                    <div className="preset-actions">
                      <button 
                        onClick={() => handleLoadPreset(name)}
                        className="load-preset-btn"
                      >
                        {t('controls.load')}
                      </button>
                      <button 
                        onClick={() => handleDeletePreset(name)}
                        className="delete-preset-btn"
                        title={t('tooltips.deletePreset')}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="modal-buttons">
              <button onClick={() => setShowLoadDialog(false)}>
                {t('controls.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <>
          <style>
            {`
              @keyframes slideInRight {
                from {
                  transform: translateX(100%);
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}
          </style>
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: toastMessage.includes('✗') ? '#dc3545' : '#28a745',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 1000,
            fontSize: '14px',
            fontWeight: '500',
            maxWidth: '300px',
            animation: 'slideInRight 0.3s ease-out'
          }}>
            {toastMessage}
          </div>
        </>
      )}
      
      {/* Debug Info Display for Mobile - Commented out */}
      {/* {(debugInfo || debugHistory.length > 0) && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '10px',
          right: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '11px',
          fontFamily: 'monospace',
          zIndex: 1000,
          maxHeight: '120px',
          overflow: 'auto',
          border: '1px solid #333'
        }}>
          <div style={{ fontSize: '10px', color: '#888', marginBottom: '4px' }}>
            Auto-Save Debug History:
          </div>
          {debugHistory.map((msg, index) => (
            <div key={index} style={{ 
              marginBottom: '2px',
              opacity: index === 0 ? 1 : 0.7 - (index * 0.2),
              color: index === 0 ? 'white' : '#ccc'
            }}>
              {msg}
            </div>
          ))}
        </div>
      )} */}
    </>
  );
};

export default SettingsControls;